/**
 * Vesting Helper Functions for Trendphile
 * Calculations and utilities for vesting progress
 */

import { ethers } from 'ethers';

// Contract constants - PRODUCTION VALUES
export const VESTING_DURATION_SECONDS = 15552000; // 6 months = 180 days = 15,552,000 seconds (180 * 24 * 60 * 60)
export const TEAM_CLIFF_DURATION_SECONDS = 15552000; // 6 months = 180 days = 15,552,000 seconds
export const TEAM_TOTAL_VESTING_SECONDS = 62208000; // 24 months = 720 days = 62,208,000 seconds (720 * 24 * 60 * 60)
export const TEAM_LINEAR_VESTING_SECONDS = 46656000; // 18 months = 540 days = 46,656,000 seconds (540 * 24 * 60 * 60)
export const SECONDS_PER_YEAR = 31536000; // 365 days = 31,536,000 seconds (365 * 24 * 60 * 60)
export const TGE_USER_THRESHOLD = 10000;
export const TGE_PREDICTION_THRESHOLD = 100000;

// Base reward rates per year (as per whitepaper)
export const PREDICTION_BASE_RATES = [500, 250, 150, 50]; // Year 1, 2, 3, 4

// Reward type configurations
export const REWARD_TYPES = {
  PROFILE: {
    name: 'Profile Completion',
    amount: 4500,
    maxAmount: 4500,
    icon: '👤',
    color: '#FFD700'
  },
  PREDICTIONS: {
    name: 'Predictions',
    yearlyAmount: PREDICTION_BASE_RATES, // [500, 250, 150, 50] per whitepaper
    minQuality: 70,
    icon: '🎯',
    color: '#FFA500'
  },
  SOCIAL: {
    name: 'Social Shares',
    amount: 100,
    dailyLimit: 5,
    icon: '📢',
    color: '#FF8C00'
  },
  REFERRALS: {
    name: 'Referrals',
    amount: 10000,
    maxReferrals: 3,
    icon: '👥',
    color: '#DAA520'
  }
};

/**
 * Determine vesting state based on backend and blockchain data
 */
export const determineVestingState = (backendData, blockchainData) => {
  if (!blockchainData?.tgeActivated) {
    return 'pre-tge';
  }
  
  if (blockchainData?.vestingComplete) {
    return 'complete';
  }
  
  // Check if vesting period has ended
  if (blockchainData?.tgeTimestamp) {
    const vestingEnd = blockchainData.tgeTimestamp + VESTING_DURATION_SECONDS;
    if (Date.now() / 1000 > vestingEnd) {
      return 'complete';
    }
  }
  
  return 'vesting';
};

/**
 * Calculate user vesting progress percentage (6 months)
 */
export const calculateVestingProgress = (startTimestamp, duration = VESTING_DURATION_SECONDS) => {
  if (!startTimestamp) return 0;
  
  const now = Math.floor(Date.now() / 1000);
  const tge = Number(startTimestamp);
  
  if (now < tge) return 0;
  
  const elapsed = now - tge;
  if (elapsed >= duration) return 100;
  
  const progress = (elapsed / duration) * 100;
  return Math.min(100, Math.max(0, progress));
};

/**
 * Calculate team vesting progress with cliff period
 * Returns progress (0-100) and cliff status
 */
export const calculateTeamVestingProgress = (tgeTimestamp) => {
  if (!tgeTimestamp) return { progress: 0, cliffEnded: false };
  
  const now = Math.floor(Date.now() / 1000);
  const tge = Number(tgeTimestamp);
  const elapsed = now - tge;
  
  // During cliff period (first 6 months): 0% progress
  if (elapsed < TEAM_CLIFF_DURATION_SECONDS) {
    return { 
      progress: 0, 
      cliffEnded: false,
      daysUntilCliff: Math.ceil((TEAM_CLIFF_DURATION_SECONDS - elapsed) / 86400)
    };
  }
  
  // After total vesting period: 100% progress
  if (elapsed >= TEAM_TOTAL_VESTING_SECONDS) {
    return { progress: 100, cliffEnded: true };
  }
  
  // Linear vesting over 18 months after cliff
  const linearElapsed = elapsed - TEAM_CLIFF_DURATION_SECONDS;
  const progress = (linearElapsed / TEAM_LINEAR_VESTING_SECONDS) * 100;
  
  return { 
    progress: Math.min(100, Math.max(0, progress)), 
    cliffEnded: true 
  };
};

/**
 * Calculate claimable vested amount for users (6-month vesting)
 */
export const calculateClaimableVested = (totalVested, startTimestamp, alreadyClaimed, duration = VESTING_DURATION_SECONDS) => {
  if (!startTimestamp || !totalVested) return 0;
  
  const now = Math.floor(Date.now() / 1000);
  const tge = Number(startTimestamp);
  
  if (now < tge) return 0;
  
  const elapsed = now - tge;
  
  // Linear vesting over 6 months
  const vestedAmount = (Number(totalVested) * Math.min(elapsed, duration)) / duration;
  const claimable = vestedAmount - Number(alreadyClaimed || 0);
  
  return Math.max(0, claimable);
};

/**
 * Get current year and base rate based on TGE timestamp
 */
export const getCurrentYearAndRate = (tgeTimestamp) => {
  if (!tgeTimestamp) return { year: 1, rate: PREDICTION_BASE_RATES[0] };
  
  const now = Math.floor(Date.now() / 1000);
  const tge = Number(tgeTimestamp);
  
  if (now < tge) return { year: 1, rate: PREDICTION_BASE_RATES[0] };
  
  const elapsed = now - tge;
  const yearsPassed = Math.floor(elapsed / SECONDS_PER_YEAR);
  const year = Math.min(yearsPassed + 1, 4); // Year 1-4
  const rate = PREDICTION_BASE_RATES[year - 1];
  
  return { year, rate };
};

/**
 * Format token amount with proper decimals
 */
export const formatTokenAmount = (amount, decimals = 18) => {
  if (!amount) return '0';
  
  try {
    const formatted = ethers.formatUnits(amount, decimals);
    const num = parseFloat(formatted);
    
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  } catch {
    return amount.toString();
  }
};

/**
 * Format date for display
 */
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Format remaining time
 */
export const formatRemainingTime = (endTimestamp) => {
  const now = Date.now() / 1000;
  const remaining = endTimestamp - now;
  
  if (remaining <= 0) return 'Complete';
  
  const days = Math.floor(remaining / 86400);
  const hours = Math.floor((remaining % 86400) / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  
  if (days > 0) {
    return `${days}d ${hours}h remaining`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m remaining`;
  }
  return `${minutes}m remaining`;
};

/**
 * Calculate TGE progress
 */
export const calculateTGEProgress = (currentUsers, currentPredictions) => {
  const usersProgress = (currentUsers / TGE_USER_THRESHOLD) * 100;
  const predictionsProgress = (currentPredictions / TGE_PREDICTION_THRESHOLD) * 100;
  
  return {
    usersProgress: Math.min(100, usersProgress),
    predictionsProgress: Math.min(100, predictionsProgress),
    overallProgress: Math.min(100, (usersProgress + predictionsProgress) / 2),
    isReady: usersProgress >= 100 && predictionsProgress >= 100
  };
};

/**
 * Calculate reward breakdown percentages
 */
export const calculateBreakdownPercentages = (breakdown) => {
  const total = Object.values(breakdown).reduce((sum, val) => sum + val, 0);
  
  if (total === 0) return {};
  
  return Object.entries(breakdown).reduce((acc, [key, value]) => {
    acc[key] = {
      amount: value,
      percentage: ((value / total) * 100).toFixed(1)
    };
    return acc;
  }, {});
};

/**
 * Get progress bar data for each reward type
 */
export const getRewardProgress = (breakdown) => {
  return [
    {
      type: 'profile',
      label: 'Profile Completion',
      current: breakdown?.profile || 0,
      max: REWARD_TYPES.PROFILE.maxAmount,
      color: REWARD_TYPES.PROFILE.color,
      icon: REWARD_TYPES.PROFILE.icon
    },
    {
      type: 'predictions',
      label: 'Predictions',
      current: breakdown?.predictions || 0,
      max: REWARD_TYPES.PREDICTIONS.yearlyAmount[0] * 100, // Rough estimate
      color: REWARD_TYPES.PREDICTIONS.color,
      icon: REWARD_TYPES.PREDICTIONS.icon
    },
    {
      type: 'socialShares',
      label: 'Social Shares',
      current: breakdown?.socialShares || 0,
      max: REWARD_TYPES.SOCIAL.amount * REWARD_TYPES.SOCIAL.dailyLimit * 365,
      color: REWARD_TYPES.SOCIAL.color,
      icon: REWARD_TYPES.SOCIAL.icon
    },
    {
      type: 'referrals',
      label: 'Referrals',
      current: breakdown?.referrals || 0,
      max: REWARD_TYPES.REFERRALS.amount * REWARD_TYPES.REFERRALS.maxReferrals,
      color: REWARD_TYPES.REFERRALS.color,
      icon: REWARD_TYPES.REFERRALS.icon
    }
  ];
};

/**
 * Generate timeline milestones (6-month vesting)
 */
export const generateMilestones = (tgeTimestamp, vestingDuration = VESTING_DURATION_SECONDS) => {
  if (!tgeTimestamp) return [];
  
  const now = Math.floor(Date.now() / 1000);
  const tge = Number(tgeTimestamp);
  
  const milestones = [
    {
      id: 'tge',
      label: 'TGE Activated',
      timestamp: tge,
      description: '50% instant unlock, 50% starts vesting',
      isComplete: now >= tge
    },
    {
      id: 'month1',
      label: '1 Month',
      timestamp: tge + (30 * 24 * 60 * 60),
      description: '~8.3% of vested unlocked (1/6 of 50%)',
      isComplete: now >= tge + (30 * 24 * 60 * 60)
    },
    {
      id: 'month3',
      label: '3 Months',
      timestamp: tge + (90 * 24 * 60 * 60),
      description: '~25% of vested unlocked (3/6 of 50%)',
      isComplete: now >= tge + (90 * 24 * 60 * 60)
    },
    {
      id: 'complete',
      label: '6 Months End',
      timestamp: tge + vestingDuration,
      description: '100% FULLY UNLOCKED - All future rewards instant',
      isComplete: now >= tge + vestingDuration
    }
  ];
  
  return milestones;
};

/**
 * Animate number counting up
 */
export const animateValue = (start, end, duration, callback) => {
  const startTime = performance.now();
  
  const update = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    
    // Ease out cubic
    const easeProgress = 1 - Math.pow(1 - progress, 3);
    const current = start + (end - start) * easeProgress;
    
    callback(current);
    
    if (progress < 1) {
      requestAnimationFrame(update);
    }
  };
  
  requestAnimationFrame(update);
};

export default {
  determineVestingState,
  calculateVestingProgress,
  calculateTeamVestingProgress,
  calculateClaimableVested,
  getCurrentYearAndRate,
  formatTokenAmount,
  formatDate,
  formatRemainingTime,
  calculateTGEProgress,
  calculateBreakdownPercentages,
  getRewardProgress,
  generateMilestones,
  animateValue,
  REWARD_TYPES,
  TGE_USER_THRESHOLD,
  TGE_PREDICTION_THRESHOLD,
  VESTING_DURATION_SECONDS,
  TEAM_CLIFF_DURATION_SECONDS,
  TEAM_TOTAL_VESTING_SECONDS,
  PREDICTION_BASE_RATES
};

